'use client';

import { useRef } from 'react';

import { useGSAP } from '@gsap/react';
import { Container, Grid, Typography } from '@mui/material';
import { gsap } from 'gsap';
import { useTranslations } from 'next-intl';

import GlowingIcon from '@/components/GlowingIcon';
import SectionTitle from '@/components/SectionTitle';
import Code from '@/components/SvgIcons/Code';
import TechCard from '@/components/TechCard';
import { techStackItems } from '@/config/tech-stack.config';
import colors from '@/styles/themes/colors';
import { loadScrollTrigger } from '@/utils/gsap/lazyPlugins';

import styles from './TechStackSection.module.scss';

const TechStackSection = () => {
  const t = useTranslations('common.techStackSection');
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const triggerRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const container = triggerRef.current;

      if (!container) return undefined;

      let isCancelled = false;

      const initScrollTrigger = async () => {
        await loadScrollTrigger();

        if (isCancelled || !container) return;

        gsap.fromTo(
          itemsRef.current,
          {
            opacity: 0,
            scale: 0.8,
            y: 30,
          },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: container,
              start: 'top 80%',
              end: 'bottom 60%',
              scrub: 1,
            },
          }
        );
      };

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            initScrollTrigger();
            observer.disconnect();
          }
        },
        { rootMargin: '200px' }
      );

      observer.observe(container);

      return () => {
        isCancelled = true;
        observer.disconnect();
      };
    },
    { scope: triggerRef }
  );

  const setItemRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) itemsRef.current[index] = el;
  };

  return (
    <Container component="section" maxWidth="xl" className={styles.container}>
      <SectionTitle
        translationNamespace="common.techStackSection"
        translationKey="title"
        icon={
          <GlowingIcon variant="pink">
            <Code size={64} fill={colors.pink600} />
          </GlowingIcon>
        }
        className={styles.title}
      />
      <Typography variant="body1" textAlign="center" className={styles.description}>
        {t('description')}
      </Typography>
      <Grid container spacing={3} ref={triggerRef}>
        {techStackItems.map((tech, index) => (
          <Grid key={tech.id} size={{ xs: 6, md: 4, lg: 2 }} ref={setItemRef(index)}>
            <TechCard tech={tech} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default TechStackSection;
